// GPX (GPS Exchange Format) parsing + mapping for Import Map (CLUB-188 / CLUB-186).
//
// GPX is XML in WGS84 lat/lon — the same coordinate system the app's GeoPt uses,
// so points import directly with no projection. We parse with the platform
// DOMParser (no dependency) and read elements by local name so a default *or*
// prefixed GPX namespace both work.

import type { GeoPt } from './types'
import { MIN_RING_POINTS } from './polygonUnion'
import gpxPinKeywords from './gpxPinKeywords.json'

export type PinKind = string

export interface GpxWaypoint {
  lat: number
  lon: number
  name: string
  sym: string | null
  /** The GPX <type> element. WeHunt's own export writes the canonical pin-type
   *  id here, so a round-trip can restore the exact type instead of re-inferring
   *  it from the name (which loses every pin whose label is not a keyword). */
  type: string | null
}

/** A GPX <trk> or <rte>: its ordered points plus the <name>/<type> that WeHunt
 *  writes so an export can round-trip each polygon back to its own layer. */
export interface GpxPath {
  points: GeoPt[]
  name: string | null
  /** The GPX <type>. WeHunt writes the layer tag here — see {@link GPX_AREA_TYPE}. */
  type: string | null
}

export interface ParsedGpx {
  /** One entry per <trk>; each track's <trkpt>s are merged across its segments. */
  tracks: GpxPath[]
  /** One entry per <rte>; the route's <rtept>s in order. */
  routes: GpxPath[]
  waypoints: GpxWaypoint[]
}

/**
 * The value WeHunt writes into a polygon's GPX <type> to record which map layer
 * it came from. Shared by the exporter (which writes it) and the importer (which
 * reads it back), so the two can never drift — a mismatch here is exactly what
 * silently dropped sub-areas and forbidden areas on import.
 */
export const GPX_AREA_TYPE = {
  border: 'border',
  subArea: 'subarea',
  forbidden: 'forbidden',
} as const

/** Thrown when the input is not valid GPX (malformed XML or not a <gpx> doc). */
export class GpxParseError extends Error {}

/** Reject files larger than this before reading — protects against huge uploads. */
export const MAX_GPX_BYTES = 20 * 1024 * 1024 // 20 MB
/** Cap the imported boundary's vertex count; denser tracks are down-sampled. */
export const MAX_BOUNDARY_POINTS = 2000

function toFinite(v: string | null): number | null {
  if (v === null) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function byTag(root: Element | Document, tag: string): Element[] {
  // Local-name match, namespace-agnostic (handles both default and prefixed GPX).
  return Array.from(root.getElementsByTagNameNS('*', tag))
}

function childText(el: Element, tag: string): string | null {
  // A DIRECT child only, matched by local name (namespace-agnostic). A <trk>,
  // <rte> or <wpt> can contain descendant elements that reuse these tag names —
  // a <trkpt>/<rtept> may carry its own <name>/<type>, and an <extensions> block
  // may nest anything — so a descendant search (getElementsByTagNameNS) would
  // read a point's tag as the whole path's name or layer and mis-bucket it.
  for (const child of Array.from(el.children)) {
    if (child.localName !== tag) continue
    const t = child.textContent?.trim()
    return t ? t : null
  }
  return null
}

function readPoint(el: Element): GeoPt | null {
  const lat = toFinite(el.getAttribute('lat'))
  const lon = toFinite(el.getAttribute('lon'))
  if (lat === null || lon === null) return null
  return { lat, lon }
}

/**
 * Parse GPX text into tracks, routes and waypoints. Throws {@link GpxParseError}
 * for malformed XML or a document whose root element is not <gpx>.
 */
export function parseGpx(text: string): ParsedGpx {
  let doc: Document
  try {
    doc = new DOMParser().parseFromString(text, 'application/xml')
  } catch {
    throw new GpxParseError('Could not parse the file as XML.')
  }
  // Browsers/jsdom surface XML syntax errors as a <parsererror> element.
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new GpxParseError('The file is not valid XML.')
  }
  const root = doc.documentElement
  if (!root || root.localName !== 'gpx') {
    throw new GpxParseError('The file is not a GPX document.')
  }

  const tracks: GpxPath[] = []
  for (const trk of byTag(root, 'trk')) {
    const points = byTag(trk, 'trkpt').map(readPoint).filter((p): p is GeoPt => p !== null)
    if (points.length > 0) tracks.push({ points, name: childText(trk, 'name'), type: childText(trk, 'type') })
  }

  const routes: GpxPath[] = []
  for (const rte of byTag(root, 'rte')) {
    const points = byTag(rte, 'rtept').map(readPoint).filter((p): p is GeoPt => p !== null)
    if (points.length > 0) routes.push({ points, name: childText(rte, 'name'), type: childText(rte, 'type') })
  }

  const waypoints: GpxWaypoint[] = []
  for (const wpt of byTag(root, 'wpt')) {
    const p = readPoint(wpt)
    if (!p) continue
    waypoints.push({ lat: p.lat, lon: p.lon, name: childText(wpt, 'name') ?? '', sym: childText(wpt, 'sym'), type: childText(wpt, 'type') })
  }

  return { tracks, routes, waypoints }
}

// ── Waypoint → pin-type inference ────────────────────────────────────────────
// Ordered rules; the `sym` (GPS symbol) and `name` are matched case-insensitively
// against keywords (English + Swedish). First match wins; unmatched → 'other',
// which the import review step lets the user remap.
//
// The table lives in gpxPinKeywords.json so it has one home in the repo: the
// mobile app applies the same inference (domains/clubhouse/wehunt-mobile,
// gpx_pin_keyword_rules.dart) and its unit test reads this JSON and fails when
// the two copies differ. Edit the JSON, then the Dart constant, never one alone.
const PIN_RULES: { kind: PinKind; keywords: string[] }[] = gpxPinKeywords

export function inferPinKind(sym: string | null, name: string): PinKind {
  const hay = `${sym ?? ''} ${name}`.toLowerCase()
  for (const rule of PIN_RULES) {
    if (rule.keywords.some((k) => hay.includes(k))) return rule.kind
  }
  return 'other'
}

/** The pin kind for an imported waypoint: an exact catalog id in <type>
 *  (WeHunt's own export) wins; otherwise infer from the sym/name keywords. */
export function suggestPinKind(
  type: string | null,
  sym: string | null,
  name: string,
  isKnownPinType: (id: string) => boolean,
): PinKind {
  if (type !== null && isKnownPinType(type)) return type
  return inferPinKind(sym, name)
}

// ── Import model for the review UI ──────────────────────────────────────────

export interface GpxImportPin {
  id: string
  name: string
  sym: string | null
  lat: number
  lon: number
  suggest: PinKind
}

/** One imported polygon (a single ring), down-sampled, tagged with its layer. */
export interface GpxImportArea {
  id: string
  name: string
  points: GeoPt[]
}

/** One untyped track or route, imported as an open trail (never closed). */
export interface GpxImportTrail {
  id: string
  name: string | null
  points: GeoPt[]
}

export interface GpxImportModel {
  /** Ground borders (GPX <type>border) — one entry per exported ring. */
  borders: GpxImportArea[]
  /** Sub-areas (GPX <type>subarea). */
  subAreas: GpxImportArea[]
  /** Forbidden areas (GPX <type>forbidden). */
  forbiddenAreas: GpxImportArea[]
  /** Every untyped track and route with at least two points, in file order —
   *  what third-party GPX (Garmin Basecamp, a handheld) is made of. */
  trails: GpxImportTrail[]
  /** The longest untyped path, down-sampled, offered as an OPT-IN hunting-ground
   *  boundary. `trailId` names the trail it was taken from, so a file imported
   *  this way does not also bring the same path in as a trail. Null when no
   *  untyped path is long enough to be a ring. */
  boundary: { trailId: string; points: GeoPt[] } | null
  pins: GpxImportPin[]
}

/** Keep at most `max` points, evenly strided, always retaining first and last. */
export function downsample(points: GeoPt[], max: number): GeoPt[] {
  if (points.length <= max) return points
  const stride = Math.ceil(points.length / max)
  const out: GeoPt[] = []
  for (let i = 0; i < points.length; i += stride) out.push(points[i])
  const last = points[points.length - 1]
  const tail = out[out.length - 1]
  if (tail.lat !== last.lat || tail.lon !== last.lon) out.push(last)
  return out
}

/**
 * Turn a parsed GPX into the reviewable import model.
 *
 * A WeHunt export tags every polygon with its layer in <type> (border / subarea
 * / forbidden), so each is restored to its own layer. Every other track and
 * route is an open path — a road, a stalking path, a walked line — and becomes a
 * trail, the way the mobile app's importer has always read them. Closing such a
 * path into a polygon draws a straight edge from its last point back to its
 * first that exists nowhere in the file, which is what customers importing
 * Basecamp roads saw. The longest of them is still offered as a boundary, but
 * only when the user asks for it. Every waypoint becomes a pin with an inferred
 * type. Areas are left open — a hunting-ground area renders closed regardless,
 * so no explicit closing point is required. A typed path with fewer than
 * {@link MIN_RING_POINTS} points is dropped: the map editor discards any ring
 * below that, so admitting it would count an area in the review that never
 * lands on the map. A trail needs two points, like a line on mobile.
 */
export function buildImportModel(parsed: ParsedGpx, isKnownPinType: (id: string) => boolean): GpxImportModel {
  const borders: GpxImportArea[] = []
  const subAreas: GpxImportArea[] = []
  const forbiddenAreas: GpxImportArea[] = []
  const trails: GpxImportTrail[] = []

  const paths = [...parsed.tracks, ...parsed.routes]
  paths.forEach((p, i) => {
    const bucket =
      p.type === GPX_AREA_TYPE.border ? borders :
      p.type === GPX_AREA_TYPE.subArea ? subAreas :
      p.type === GPX_AREA_TYPE.forbidden ? forbiddenAreas :
      null
    if (bucket === null) {
      if (p.points.length >= 2) trails.push({ id: `gpx-trail-${i}`, name: p.name, points: p.points })
      return
    }
    if (p.points.length < MIN_RING_POINTS) return
    bucket.push({
      id: `gpx-area-${i}`,
      name: p.name ?? '',
      points: downsample(p.points, MAX_BOUNDARY_POINTS),
    })
  })

  // The boundary candidate: the longest trail that is long enough to be a ring.
  const longest = trails
    .filter((tr) => tr.points.length >= MIN_RING_POINTS)
    .reduce<GpxImportTrail | null>(
      (best, tr) => (best === null || tr.points.length > best.points.length ? tr : best),
      null,
    )
  const boundary = longest ? { trailId: longest.id, points: downsample(longest.points, MAX_BOUNDARY_POINTS) } : null

  const pins: GpxImportPin[] = parsed.waypoints.map((w, i) => ({
    id: `gpx-wpt-${i}`,
    name: w.name || `Waypoint ${i + 1}`,
    sym: w.sym,
    lat: w.lat,
    lon: w.lon,
    suggest: suggestPinKind(w.type, w.sym, w.name, isKnownPinType),
  }))

  return { borders, subAreas, forbiddenAreas, trails, boundary, pins }
}

export function isEmptyModel(model: GpxImportModel): boolean {
  return (
    model.trails.length === 0 &&
    model.borders.length === 0 &&
    model.subAreas.length === 0 &&
    model.forbiddenAreas.length === 0 &&
    model.pins.length === 0
  )
}
